import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UtensilsCrossed, Image, MessageSquare, Mail, LogOut, LayoutDashboard, Globe, Gift, Home, Star, Menu, X, FileText, Heart, Inbox } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/admin/menu", label: "Carte & Plats", icon: UtensilsCrossed },
  { to: "/admin/gallery", label: "Galerie", icon: Image },
  { to: "/admin/home-images", label: "Photos accueil", icon: Home },
  { to: "/admin/reviews", label: "Avis clients", icon: Star },
  { to: "/admin/pages", label: "Pages du site", icon: FileText },
  { to: "/admin/seo", label: "Pages SEO", icon: Globe },
  { to: "/admin/giftcards", label: "Cartes Cadeaux", icon: Gift },
  { to: "/admin/events", label: "Demandes événements", icon: MessageSquare },
  { to: "/admin/receptions", label: "Réceptions", icon: Heart },
  { to: "/admin/reception-emails", label: "Emails & imports", icon: Inbox },
  { to: "/admin/messages", label: "Messages contact", icon: Mail },
];

function NavItems({ onNavigate, testidPrefix = "admin-nav" }) {
  return (
    <>
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`${testidPrefix}-${n.to.replace("/admin", "") || "dashboard"}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? "bg-[#203240]/30 text-[#B8956A] border-l-2 border-[#B8956A]" : "text-[#203240]/80 hover:bg-[#EFE9DC]"}`
          }
        >
          <n.icon size={16} />
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Section G: /admin and every sub-route MUST be marked noindex, nofollow.
  // We upsert the robots meta and reset it on unmount so public pages remain
  // indexable.
  useEffect(() => {
    const prev = document.head.querySelector('meta[name="robots"]');
    const prevContent = prev ? prev.getAttribute("content") : null;
    let el = prev;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    return () => {
      if (prevContent != null) el.setAttribute("content", prevContent);
      else el.remove();
    };
  }, []);

  // Auto-close mobile drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  const handleLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  const currentTitle = NAV.find((n) => n.to === location.pathname || (n.end && location.pathname === n.to))?.label
    || (location.pathname.startsWith("/admin/") ? "" : "Back-office");

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#F6F0E7] text-[#203240] flex">
      {/* Desktop sidebar */}
      <aside className="w-64 hidden md:flex flex-col bg-[#FFFFFF] border-r border-[#D9D3C5] fixed inset-y-0 left-0 z-30">
        <div className="p-6 border-b border-[#D9D3C5]">
          <p className="font-display text-2xl">L'Entracte</p>
          <p className="overline mt-1">Back-office</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <NavItems />
        </nav>
        <div className="p-4 border-t border-[#D9D3C5]">
          <p className="text-xs text-[#697585] truncate">{user?.email}</p>
          <button onClick={handleLogout} data-testid="admin-logout" className="mt-3 flex items-center gap-2 text-sm text-[#203240]/80 hover:text-[#B8956A]">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="admin-mobile-overlay"
          className="md:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        data-testid="admin-mobile-drawer"
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-[#FFFFFF] border-r border-[#D9D3C5] flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="p-5 border-b border-[#D9D3C5] flex items-center justify-between">
          <div>
            <p className="font-display text-2xl">L'Entracte</p>
            <p className="overline mt-1">Back-office</p>
          </div>
          <button
            data-testid="admin-mobile-close"
            onClick={() => setOpen(false)}
            aria-label="Fermer le menu"
            className="p-2 text-[#203240]/70 hover:text-[#B8956A]"
          >
            <X size={22} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <NavItems onNavigate={() => setOpen(false)} testidPrefix="admin-mobile-nav" />
        </nav>
        <div className="p-4 border-t border-[#D9D3C5]">
          <p className="text-xs text-[#697585] truncate">{user?.email}</p>
          <button onClick={handleLogout} data-testid="admin-mobile-logout" className="mt-3 flex items-center gap-2 text-sm text-[#203240]/80 hover:text-[#B8956A]">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-20 bg-[#FFFFFF]/95 backdrop-blur border-b border-[#D9D3C5] flex items-center justify-between px-4 py-3">
          <button
            data-testid="admin-mobile-toggle"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir le menu"
            className="p-2 -ml-2 text-[#203240] hover:text-[#B8956A]"
          >
            <Menu size={22} />
          </button>
          <p className="font-display text-lg text-[#203240] truncate max-w-[65%] text-center">
            {currentTitle || "L'Entracte"}
          </p>
          <button
            onClick={handleLogout}
            data-testid="admin-mobile-logout-top"
            aria-label="Déconnexion"
            className="p-2 -mr-2 text-[#203240]/70 hover:text-[#B8956A]"
          >
            <LogOut size={18} />
          </button>
        </header>
        <div className="p-4 sm:p-6 md:p-10 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
